import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import HistoryIcon from "@mui/icons-material/History";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";

import LoanOtpDialog from "../components/LoanOtpDialog";
import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import { fetchSystemSettings } from "../services/systemSettingsService";
import {
  createLoan,
  getMemberLoans,
  resendLoanOtp,
  verifyLoanOtp,
} from "../services/loanService";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const currencyFmt = (value) =>
  new Intl.NumberFormat("en-TZ", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const statusColor = (status) => {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "APPROVED_FOR_DISBURSEMENT":
      return "info";
    case "PENDING_CHAIR_APPROVAL":
    case "PENDING_SECRETARY_APPROVAL":
    case "PENDING_ACCOUNTANT_APPROVAL":
      return "warning";
    case "REJECTED":
      return "error";
    case "CLOSED":
      return "default";
    default:
      return "default";
  }
};

const statusLabel = (status) => {
  switch (status) {
    case "PENDING_CHAIR_APPROVAL":
      return "Pending Chair Approval";
    case "PENDING_SECRETARY_APPROVAL":
      return "Pending Secretary Approval";
    case "PENDING_ACCOUNTANT_APPROVAL":
      return "Pending Accountant Approval";
    case "APPROVED_FOR_DISBURSEMENT":
      return "Approved For Disbursement";
    case "ACTIVE":
      return "Active";
    case "REJECTED":
      return "Rejected";
    case "CLOSED":
      return "Closed";
    default:
      return status || "Unknown";
  }
};

const extractLoanApiMessage = (error) => {
  if (error?.payload?.detail) return error.payload.detail;
  const raw = error?.message || "Unexpected loan service error.";
  return raw.replace(/^API request failed:\s*/, "");
};

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const LoanApplication = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [maxLoanTermMonths, setMaxLoanTermMonths] = useState(12);
  const [loanOtpState, setLoanOtpState] = useState(null);
  const [loanOtpError, setLoanOtpError] = useState("");
  const [isVerifyingLoanOtp, setIsVerifyingLoanOtp] = useState(false);
  const [isResendingLoanOtp, setIsResendingLoanOtp] = useState(false);
  const [form, setForm] = useState({
    principal_amount: "",
    term_months: "",
    term_type: "MONTHLY",
  });
  const [formErrors, setFormErrors] = useState({});

  const refreshLoans = async () => {
    if (!user?.member_id) return;
    const response = await getMemberLoans(user.member_id);
    const list = Array.isArray(response) ? response : response?.results || [];
    setLoans(list);
  };

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      if (!user?.member_id) {
        setLoading(false);
        return;
      }
      try {
        const [settingsPayload, loansPayload] = await Promise.all([
          fetchSystemSettings().catch(() => null),
          getMemberLoans(user.member_id),
        ]);
        if (!active) return;
        const maxTerm = Number(settingsPayload?.max_loan_term_months);
        if (!Number.isNaN(maxTerm) && maxTerm > 0) {
          setMaxLoanTermMonths(maxTerm);
        }
        setLoans(Array.isArray(loansPayload) ? loansPayload : loansPayload?.results || []);
      } catch (error) {
        if (!active) return;
        Swal.fire("Error", `Failed to load loan data: ${extractLoanApiMessage(error)}`, "error");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, [user?.member_id]);

  const portfolioSummary = useMemo(() => {
    const activeLoans = loans.filter((loan) => loan.status === "ACTIVE");
    const pendingLoans = loans.filter((loan) => String(loan.status || "").startsWith("PENDING_"));
    return {
      activeCount: activeLoans.length,
      pendingCount: pendingLoans.length,
      totalOutstanding: activeLoans.reduce(
        (sum, loan) => sum + Number(loan.outstanding_amount || 0),
        0
      ),
    };
  }, [loans]);

  const handleFormChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }));
  };

  const handleSubmit = async () => {
    const errors = {};
    const principal = Number(form.principal_amount);
    const termMonths = Number(form.term_months);

    if (!principal || principal <= 0) {
      errors.principal_amount = "Enter a valid loan amount.";
    }
    if (!termMonths || termMonths <= 0) {
      errors.term_months = "Enter a valid repayment duration.";
    } else if (termMonths > maxLoanTermMonths) {
      errors.term_months = `Loan term cannot exceed ${maxLoanTermMonths} months.`;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setLoanOtpError("");
    try {
      const pendingResponse = await createLoan({
        principal_amount: principal,
        term_months: termMonths,
        term_type: form.term_type,
      });
      setLoanOtpState(pendingResponse);
    } catch (error) {
      const payload = error?.payload;
      if (payload && typeof payload === "object" && !Array.isArray(payload) && !payload.detail) {
        setFormErrors(payload);
      }
      Swal.fire("Unable to continue", extractLoanApiMessage(error), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyLoanOtp = async (otpCode) => {
    if (!loanOtpState?.pending_request_id) return;
    if (!otpCode || otpCode.length !== 6) {
      setLoanOtpError("Enter the 6-digit OTP sent to your registered contact(s).");
      return;
    }

    setIsVerifyingLoanOtp(true);
    setLoanOtpError("");
    try {
      await verifyLoanOtp({
        pending_request_id: loanOtpState.pending_request_id,
        otp_code: otpCode,
      });
      await refreshLoans();
      setLoanOtpState(null);
      setForm({ principal_amount: "", term_months: "", term_type: "MONTHLY" });
      Swal.fire({
        icon: "success",
        title: "Loan request submitted",
        text: "OTP verified successfully. Your loan is now in the standard approval workflow.",
        confirmButtonColor: brand,
      }).then(() => {
        navigate("/app/loan-approvals");
      });
    } catch (error) {
      const payload = error?.payload || {};
      if (payload.attempts_left !== undefined) {
        setLoanOtpState((prev) => (
          prev ? { ...prev, attempts_left: Number(payload.attempts_left) } : prev
        ));
      }
      setLoanOtpError(extractLoanApiMessage(error));
    } finally {
      setIsVerifyingLoanOtp(false);
    }
  };

  const handleResendLoanOtp = async () => {
    if (!loanOtpState?.pending_request_id) return;
    setIsResendingLoanOtp(true);
    setLoanOtpError("");
    try {
      const response = await resendLoanOtp({ pending_request_id: loanOtpState.pending_request_id });
      setLoanOtpState(response);
    } catch (error) {
      const payload = error?.payload || {};
      setLoanOtpState((prev) =>
        prev
          ? {
              ...prev,
              attempts_left:
                payload.attempts_left !== undefined
                  ? Number(payload.attempts_left)
                  : prev.attempts_left,
            }
          : prev
      );
      setLoanOtpError(extractLoanApiMessage(error));
    } finally {
      setIsResendingLoanOtp(false);
    }
  };

  const handleCloseLoanOtpDialog = () => {
    setLoanOtpState(null);
    setLoanOtpError("");
    setIsVerifyingLoanOtp(false);
    setIsResendingLoanOtp(false);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand, mb: 0.5 }}>
          Secure Loan Request
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Submit your loan request, verify it with a one-time password, then the system continues with the normal approval workflow.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <MonetizationOnIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Active Loans</Typography>
                  <Typography variant="h6" fontWeight={900}>{portfolioSummary.activeCount}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <HourglassTopOutlinedIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Pending Requests</Typography>
                  <Typography variant="h6" fontWeight={900}>{portfolioSummary.pendingCount}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <HistoryIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Outstanding Balance</Typography>
                  <Typography variant="h6" fontWeight={900}>{currencyFmt(portfolioSummary.totalOutstanding)} TZS</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ borderRadius: 4, boxShadow: 4, overflow: "hidden" }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ShieldOutlinedIcon sx={{ color: brand }} />
                    <Box>
                      <Typography variant="h6" fontWeight={900}>Request a Loan</Typography>
                      <Typography variant="body2" color="text.secondary">
                        OTP verification is required before a loan request is created.
                      </Typography>
                    </Box>
                  </Stack>

                  <Alert severity="info" variant="outlined">
                    For your security, the system stores your request temporarily, sends a single-use OTP, and only creates the loan after successful verification.
                  </Alert>

                  <TextField
                    label="Loan Amount"
                    fullWidth
                    type="number"
                    value={form.principal_amount}
                    onChange={handleFormChange("principal_amount")}
                    error={Boolean(formErrors.principal_amount)}
                    helperText={formErrors.principal_amount}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
                    }}
                  />

                  <TextField
                    label="Repayment Duration (Months)"
                    fullWidth
                    type="number"
                    value={form.term_months}
                    onChange={handleFormChange("term_months")}
                    error={Boolean(formErrors.term_months)}
                    helperText={formErrors.term_months || `Maximum ${maxLoanTermMonths} months based on group policy.`}
                  />

                  <TextField
                    label="Repayment Method"
                    select
                    fullWidth
                    value={form.term_type}
                    onChange={handleFormChange("term_type")}
                  >
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                  </TextField>

                  {formErrors.form ? <Alert severity="error">{formErrors.form}</Alert> : null}

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{
                      py: 1.5,
                      fontWeight: 700,
                      bgcolor: brand,
                      "&:hover": { bgcolor: brandHover },
                    }}
                  >
                    {submitting ? "Preparing OTP..." : "Continue with OTP"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} lg={7}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ borderRadius: 4, boxShadow: 4 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                  <Typography variant="h6" fontWeight={900}>Recent Loan Requests</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track the progress of your verified loan requests and existing obligations.
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Loan ID</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Outstanding</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5}><LinearProgress /></TableCell>
                        </TableRow>
                      ) : loans.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                            No loan requests found yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        loans.map((loan) => (
                          <TableRow key={loan.id} hover>
                            <TableCell>{loan.created_at || "-"}</TableCell>
                            <TableCell>{loan.id}</TableCell>
                            <TableCell>{currencyFmt(loan.principal_amount)} TZS</TableCell>
                            <TableCell>
                              <Chip label={statusLabel(loan.status)} color={statusColor(loan.status)} size="small" />
                            </TableCell>
                            <TableCell align="right">{currencyFmt(loan.outstanding_amount)} TZS</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      <LoanOtpDialog
        open={Boolean(loanOtpState)}
        otpState={loanOtpState}
        error={loanOtpError}
        verifying={isVerifyingLoanOtp}
        resending={isResendingLoanOtp}
        onClose={handleCloseLoanOtpDialog}
        onVerify={handleVerifyLoanOtp}
        onResend={handleResendLoanOtp}
      />
    </Box>
  );
};

export default LoanApplication;
